import { LitElement, html } from 'lit';
import { api } from '../api.js';

/**
 * /personal/appearance — hoe de applicatie ERUITZIET, alleen voor jezelf.
 *
 * Bewust een eigen pagina naast Algemeen. Daar staat wie je bent en hoe je inlogt: dingen die
 * anderen zien of die je account beveiligen. Hier staat wat alleen jij ziet en wat je zonder
 * gevolgen aan en uit kunt zetten. Die twee door elkaar zetten maakt van Algemeen een la waar
 * alles in verdwijnt.
 *
 * Voorkeuren worden per gebruiker op de server bewaard (`/personal/preferences`), niet in de
 * browser: je werkt op kantoor op een andere machine dan thuis, en een weergave die je zelf
 * hebt uitgezet hoort niet terug te komen omdat je een ander apparaat pakt.
 */

/** Sleutels in de voorkeuren. Ook gelezen door app-main; wijzig ze op één plek. */
export const BREADCRUMBS_PREF = 'ui.breadcrumbs';
export const FANCY_BG_PREF = 'ui.fancyBackground';

export class PersonalAppearance extends LitElement {
  createRenderRoot() { return this; }

  static properties = {
    _loading: { type: Boolean, state: true },
    _crumbs: { type: Boolean, state: true },
    _fancy: { type: Boolean, state: true },
    _saving: { type: Boolean, state: true },
    _error: { type: String, state: true },
  };

  constructor() {
    super();
    this._loading = true;
    // Standaard AAN. Wie de voorkeur nooit heeft aangeraakt hoort de applicatie te zien zoals
    // hij bedoeld is; uitzetten is een bewuste keuze, geen toestand waar je in kunt rollen.
    this._crumbs = true;
    // Standaard UIT. Een sfeerfoto achter je werk is een smaakkwestie; wie hem niet kiest
    // hoort hem niet te krijgen.
    this._fancy = false;
    this._saving = false;
    this._error = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      const prefs = await api.getPreferences();
      // Alleen een expliciete `false` zet hem uit: een ontbrekende of kapotte waarde hoort
      // terug te vallen op de standaard, niet op 'uit'.
      this._crumbs = prefs?.[BREADCRUMBS_PREF] !== false;
      // Spiegelbeeld van hierboven: alleen een expliciete `true` zet hem aan.
      this._fancy = prefs?.[FANCY_BG_PREF] === true;
    } catch (_) {
      this._crumbs = true;
      this._fancy = false;
    } finally {
      this._loading = false;
    }
  }

  /**
   * Eén voorkeur omzetten. Eén functie voor alle schakelaars: het gedrag eromheen (meteen
   * doorgeven, terugdraaien bij een fout) hoort voor elke opmaakkeuze hetzelfde te zijn.
   */
  async _toggle(key, prop, next) {
    this[prop] = next;
    this._saving = true;
    this._error = null;
    try {
      await api.setPreference(key, next);
      // Meteen doorgeven aan de schil, zodat je het resultaat ziet zonder te herladen. Een
      // opmaakvoorkeur waarvan je het effect pas later ziet, voelt alsof hij niet werkte.
      window.dispatchEvent(new CustomEvent('preferences-updated', { detail: { [key]: next } }));
    } catch (e) {
      this[prop] = !next;   // terugdraaien: de schakelaar moet tonen wat er echt is opgeslagen
      this._error = 'Opslaan mislukt. Probeer het opnieuw.';
    } finally {
      this._saving = false;
    }
  }

  /** Schakelaar in de stijl van de rest van de instellingen: label links, knop rechts. */
  _switch(on, onChange) {
    return html`
      <button
        role="switch"
        aria-checked=${on ? 'true' : 'false'}
        ?disabled=${this._saving}
        @click=${() => onChange(!on)}
        style="flex:0 0 auto; width:46px; height:26px; border-radius:99px; border:none; cursor:pointer; padding:3px;
               display:inline-flex; justify-content:${on ? 'flex-end' : 'flex-start'}; align-items:center;
               background:${on ? 'var(--mv-primary)' : 'var(--mv-border)'}; transition:background .15s;
               opacity:${this._saving ? 0.6 : 1};">
        <span style="width:20px; height:20px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,0.25);"></span>
      </button>`;
  }

  render() {
    const rowWrap = 'display:flex; gap:24px; align-items:center; padding:20px 22px; border-top:1px solid var(--mv-border);';
    const rowLabel = 'font-weight:700; font-size:0.92rem; color:var(--mv-text); margin-bottom:2px;';
    const rowDesc = 'font-size:0.82rem; color:var(--mv-text-sub,#6c757d);';

    return html`
      <div style="max-width:800px; margin:0 auto;">
        <h1 style="margin:0 0 4px; font-size:1.5rem; font-weight:700; color:var(--mv-text);">Opmaak</h1>
        <div style="color:var(--mv-text-sub,#6c757d); font-size:0.92rem; margin-bottom:20px;">
          Hoe de applicatie eruitziet. Deze instellingen gelden alleen voor jou.
        </div>

        ${this._loading ? html`
          <div style="padding:2rem; text-align:center; color:var(--mv-text-sub,#6c757d);">Laden…</div>
        ` : html`
          <div style="border:1px solid var(--mv-border); border-radius:var(--mv-radius); background:var(--mv-surface,#fff); overflow:hidden;">
            <div style="${rowWrap} border-top:none;">
              <div style="flex:1;">
                <div style="${rowLabel}">Kruimelpad in de topbalk</div>
                <div style="${rowDesc}">
                  Het pad onder de topbalk laat zien waar je bent en waar je vandaan komt, en help makkelijker te navigeren.
                </div>
              </div>
              ${this._switch(this._crumbs, (v) => this._toggle(BREADCRUMBS_PREF, '_crumbs', v))}
            </div>

            <div style="${rowWrap}">
              <div style="flex:1;">
                <div style="${rowLabel}">Sfeervolle achtergrond</div>
                <div style="${rowDesc}">
                  Zet een vage veldfoto achter de pagina's, dezelfde als op het inlogscherm.
                </div>
              </div>
              ${this._switch(this._fancy, (v) => this._toggle(FANCY_BG_PREF, '_fancy', v))}
            </div>
          </div>

          ${this._error ? html`
            <div style="margin-top:12px; padding:10px 14px; border-radius:8px; background:var(--mv-danger-bg,rgba(239,68,68,0.08)); color:var(--mv-danger,#dc3545); font-size:0.88rem;">
              ${this._error}
            </div>` : ''}
        `}
      </div>`;
  }
}

customElements.define('personal-appearance', PersonalAppearance);
